import { useCallback, useEffect, useState } from 'react'
import { ButtonsModalSmallCentered, ModalBigCentered } from '../../../../../../baseComponents/Modal'
import { RowData } from '../../../../../../baseComponents/TwinTable/types'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import { BeforeSubmitHandler, OnSubmit } from '../../../../../../forms/TwinForm/types'
import { SetEditDeleteModalTy } from '../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { twinFetchPostJSON, twinFetchPostPrinter } from '../../../../../../utils/globals/data'
import TwinForm from '../../../../../../forms/TwinForm'
import { InputHidden } from '../../../../../../forms/Input'
import useIsLoading from '../../../../../../utils/hooks/useIsLoading'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import TableWithoutVirtualListing from '../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import Checkbox from '../../../../../../forms/Checkbox'


interface ModalPrintMultipleWorkSheetTagsProps extends ModalPrintMultipleWorkSheetTagsLogicProps {
}

interface PrintProductsParams {
    WorkSheetProductId: string,
    productNameReference: string,
    selected: boolean
}

const ModalPrintMultipleWorkSheetTags: React.FC<ModalPrintMultipleWorkSheetTagsProps> = ({ ...logic }) => {
    const { t } = useTwinTranslation()
    const { mySetOpened, loading, myOnSubmit, onPreSubmit, printProducts, handleSelectSingleProduct, selectAllProducts } = useModalPrintMultipleWorkSheetTagsLogic({ ...logic })
    const columns: ColumnTableSchema<PrintProductsParams> = [
        {
            id: 'productNameReference',
            dataKey: 'productNameReference',
            label: t('product', 'Producto'),
        },
        {
            id: 'selected',
            dataKey: 'selected',
            label: () => <Checkbox label={''} onClick={selectAllProducts} />,
            customRender: (value: any, allRowData) => <Checkbox label={''} checked={value === true} onClick={() => handleSelectSingleProduct(allRowData.absoluteIndex || allRowData.rowIndex)} />,
            width: 50,
        },
    ]
    return (
        <ModalBigCentered opened={true} setOpened={() => mySetOpened()} onClickOut={false} className='flex_height flex-auto flex'>
            <TwinForm beforeSubmitHandler={onPreSubmit} action={'/api/app/order/sell/workSheet/printWorkSheetLabels'} onSubmit={myOnSubmit} className='flex flex-auto flex-col justify-between ' triggerErrors={false} triggerSuccess={false}>
                <h2 className='mb-20'>
                    <TwinTrans transKey={'statusHasAssociatedSellTag'}>Este estado tiene una etiqueta asociada</TwinTrans>
                </h2>
                <span className='my-20'><TwinTrans transKey={'selectProductsWantToPrint'}>Selecciona los productos que quieres imprimir</TwinTrans></span>
                <div className='flex flex-col flex-auto h-1 min-h-200'>
                    <TableWithoutVirtualListing data={printProducts} columns={columns} />
                </div>
                <InputHidden name='id' value={logic.allRowData?.id} />
                <InputHidden name='LabelId' value={logic.allRowData?.LabelId} />
                <InputHidden name='SpecificPriceRuleId' value={logic.allRowData?.SpecificPriceRuleId} />
                <ButtonsModalSmallCentered confirmText={t('print', 'Imprimir')} setOpened={() => { }} onCancel={mySetOpened} loading={loading} />
            </TwinForm>
        </ModalBigCentered>
    )
}

interface ModalPrintMultipleWorkSheetTagsLogicProps {
    allRowData: RowData
    setOpenModal: SetEditDeleteModalTy
    onSubmit: OnSubmit
}

const useModalPrintMultipleWorkSheetTagsLogic = ({ setOpenModal, allRowData, onSubmit }: ModalPrintMultipleWorkSheetTagsLogicProps) => {
    const [printProducts, setPrintProducts] = useState<PrintProductsParams[] | null>(null)
    const { startLoading, endLoading, loading } = useIsLoading()
    const { t } = useTwinTranslation()
    const errorMessage = t('couldNotConnectWithPrinter', 'No se ha podido conectar con la impresora')

    const getPrintProducts = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/order/sell/workSheet/workSheetProduct/getAllWorkSheetProducts', { where: { 'WorkSheetProduct.id': { '$or': allRowData?.workSheetProductsIds } } })
        if (result) {
            const myResult = []
            for (const element of result) {
                myResult.push({
                    WorkSheetProductId: element.id,
                    productNameReference: element?.Product?.ProductLangs?.[0]?.name + ' - ' + element.Product.reference,
                    selected: false
                })
            }
            setPrintProducts(myResult)
        }
    }, [setPrintProducts, allRowData?.workSheetProductsIds])

    const onPreSubmit: BeforeSubmitHandler = useCallback((vals) => {
        const selectedProductsForPrint: number[] = []
        for (const element of printProducts || []) {
            if (element.selected) {
                selectedProductsForPrint.push(parseInt(element.WorkSheetProductId))
            }
        }
        vals.extraProductsWhere = { id: selectedProductsForPrint }
        startLoading()
        return true
    }, [startLoading, printProducts])

    const mySetOpened = useCallback(() => {
        if (allRowData?.extraParamsForMatch && Object.keys(allRowData?.extraParamsForMatch).length) {
            setOpenModal({ type: 'edit', allRowData: { ...allRowData.extraParamsForMatch } })
        } else {
            onSubmit?.(false as any, {})
            setOpenModal(null)
        }
    }, [setOpenModal, allRowData, onSubmit])

    const myOnSubmit: OnSubmit = useCallback(async (result) => {
        if (result && result.status === 367) {
            const body = await result.json()
            const myresult = await twinFetchPostPrinter('/printLabel', body, errorMessage)
            if (myresult) {
                mySetOpened()
            }
        }
        endLoading()
    }, [endLoading, mySetOpened, errorMessage])

    const selectAllProducts = useCallback(() => {
        setPrintProducts((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            for (const element of copyOld) {
                element.selected = true
            }
            return copyOld
        })
    }, [setPrintProducts])

    const handleSelectSingleProduct = useCallback((rowIndex: any) => {
        setPrintProducts((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            const isSelected = copyOld[rowIndex].selected
            copyOld[rowIndex].selected = !isSelected
            return copyOld
        })
    }, [setPrintProducts])

    useEffect(() => {
        getPrintProducts()
    }, [getPrintProducts])

    return { mySetOpened, myOnSubmit, onPreSubmit, loading, printProducts, handleSelectSingleProduct, selectAllProducts }
}

export default ModalPrintMultipleWorkSheetTags